import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AttachFile,
  CheckCircle,
  Close,
  CloudUpload,
  Error as ErrorIcon,
  ExpandMore,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MUILink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { TransactionAsset, TransactionLogDetail } from './mockTransactionLog';
import { resolveDisplayAssetsForTransaction } from './maintenanceAssetGrouping';
import { TransactionTypeChip } from './TransactionTypeChip';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { useNotifications } from '@/stores/useNotificationStore';
import { useUploadTransactionDocument } from '@/hooks/mutations/transactions/useUploadTransactionDocument';
import { useUpdateTransactionNotes } from '@/hooks/mutations/transactions/useUpdateTransactionNotes';
import { useTransactionLogDetail } from '@/hooks/useTransactionLog';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { useAuth } from '@/hooks/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { getErrorMessage } from '@/utils/httpClient';
import {
  resolveWorkflowSourceLabel,
  shouldHideDocumentTypeChip,
  shouldHideTransactionTypeForSource,
} from './sourceWorkflow';
import { DocumentPayloadsSection } from './DocumentPayloadsSection';

export type TransactionDetailSection =
  | 'summary'
  | 'context'
  | 'assets'
  | 'documents'
  | 'payloads'
  | 'upload';

const BASE_SECTIONS: TransactionDetailSection[] = [
  'summary',
  'context',
  'assets',
  'documents',
  'upload',
];

const sectionTitle: Record<TransactionDetailSection, string> = {
  summary: 'Transaction Summary',
  context: 'Transaction Context',
  assets: 'Assets Involved',
  documents: 'Documents',
  payloads: 'Document Payloads',
  upload: 'Upload Additional Documents',
};

const resolveDocumentTypeBg = (documentType: TransactionLogDetail['documentType']): string => {
  if (documentType === 'Hand Receipt' || documentType === 'Front Window') {
    return 'primary.main';
  }
  return 'secondary.main';
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 220, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {value}
      </Typography>
    </Box>
  );
};

/** Single asset card within the Assets Involved section */
const AssetCard: React.FC<{
  asset: TransactionAsset;
  displayReceiptTypes?: ('PHR' | 'THR')[];
}> = ({ asset, displayReceiptTypes }) => {
  const hasPricing =
    asset.isBuyProgram || asset.actualPrice != null || asset.tax != null || asset.shipping != null;
  const lineTotal =
    hasPricing && asset.actualPrice != null
      ? ((asset.actualPrice ?? 0) + (asset.tax ?? 0) + (asset.shipping ?? 0)) * asset.transQty
      : null;

  // Per-asset receipt type takes precedence; fall back to transaction-level only when
  // there is exactly one type (non-ISSUE_OUT transactions have a single transaction-level type).
  const assetReceiptType: 'PHR' | 'THR' | null =
    asset.handReceiptType ??
    (displayReceiptTypes?.length === 1 ? (displayReceiptTypes[0] ?? null) : null);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" fontWeight={800}>
            {asset.nomenclature}
          </Typography>
          {assetReceiptType && (
            <Chip
              size="small"
              label={assetReceiptType}
              variant="outlined"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.05em',
                height: 20,
                '& .MuiChip-label': { px: 1 },
                ...(assetReceiptType === 'PHR'
                  ? {
                      borderColor: 'success.main',
                      color: 'success.main',
                      bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(63, 127, 85, 0.18)' : 'success.50',
                    }
                  : {
                      borderColor: 'warning.main',
                      color: 'warning.main',
                      bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(193, 125, 31, 0.18)' : 'warning.50',
                    }),
              }}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          LIN: {asset.lin} | NSN: {asset.nsn} | Qty: {asset.transQty}
        </Typography>
        {hasPricing && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              Price: ${(asset.actualPrice ?? 0).toFixed(2)}/ea
            </Typography>
            {(asset.tax ?? 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                Tax: ${(asset.tax ?? 0).toFixed(2)}
              </Typography>
            )}
            {(asset.shipping ?? 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                Shipping: ${(asset.shipping ?? 0).toFixed(2)}
              </Typography>
            )}
            {lineTotal != null && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Line Total: ${lineTotal.toFixed(2)}
              </Typography>
            )}
          </>
        )}
        {!hasPricing && (
          <>
            <Typography variant="body2" color="text.secondary">
              Owner Balance: Beg: {asset.ownerBegBal === 'Planned' ? '—' : asset.ownerBegBal} → Close: {asset.ownerCloseBal === 'Planned' ? '—' : asset.ownerCloseBal}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Receiver Balance: Beg: {asset.receiverBegBal === 'Planned' ? '—' : asset.receiverBegBal} → Close: {asset.receiverCloseBal === 'Planned' ? '—' : asset.receiverCloseBal}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Group assets by document number AND expendable series for visual organization.
 *
 * All assets in a transaction share ONE document number, but each asset retains its
 * individual expendable classification label (non_expendable or expendable) for display.
 */
const AssetsInvolvedContent: React.FC<{
  assets: TransactionAsset[];
  transactionType: TransactionLogDetail['transactionType'];
  displayReceiptTypes?: ('PHR' | 'THR')[];
}> = ({ assets, transactionType, displayReceiptTypes }) => {
  // First resolve display assets (handles maintenance transaction grouping)
  const displayAssets = useMemo(
    () => resolveDisplayAssetsForTransaction(assets, transactionType),
    [assets, transactionType],
  );

  // Group assets by document number + series combination
  // This allows assets with same doc number but different expendable classifications to be shown separately
  const groupedAssets = useMemo(() => {
    const groups = new Map<string, TransactionAsset[]>();

    for (const asset of displayAssets) {
      // Create composite key: documentNumber|series (e.g., "6023-4001|non_expendable")
      const docNum = asset.documentNumber ?? '';
      const series = asset.documentNumberSeries ?? '';
      const key = docNum ? `${docNum}|${series}` : '';
      const existing = groups.get(key) ?? [];
      existing.push(asset);
      groups.set(key, existing);
    }

    // Sort groups: document numbers first (sorted), then assets without document numbers
    // Within same doc number, non_expendable (4000 series) comes before expendable (5000 series)
    const sortedEntries = Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === '' && b !== '') return 1; // Empty goes last
      if (a !== '' && b === '') return -1;
      // Extract doc number and series from composite key
      const [docA, seriesA] = a.split('|');
      const [docB, seriesB] = b.split('|');
      // Sort by doc number first
      const docCompare = (docA ?? '').localeCompare(docB ?? '');
      if (docCompare !== 0) return docCompare;
      // Within same doc number, non_expendable (4000 series) comes first
      if (seriesA === 'non_expendable' && seriesB !== 'non_expendable') return -1;
      if (seriesA !== 'non_expendable' && seriesB === 'non_expendable') return 1;
      return 0;
    });

    return sortedEntries;
  }, [displayAssets]);

  // Check if any assets have document numbers
  const hasDocumentNumbers = groupedAssets.some(([key]) => key !== '');

  // If no assets have document numbers, show flat list
  if (!hasDocumentNumbers) {
    return (
      <Stack spacing={1.25}>
        {displayAssets.map((asset) => (
          <AssetCard key={asset.assetId} asset={asset} displayReceiptTypes={displayReceiptTypes} />
        ))}
      </Stack>
    );
  }

  // Show grouped by document number + series
  return (
    <Stack spacing={2}>
      {groupedAssets.map(([compositeKey, groupAssets]) => {
        // Extract document number from composite key (format: "docNumber|series" or "")
        const documentNumber = compositeKey ? compositeKey.split('|')[0] : '';
        const series = groupAssets[0]?.documentNumberSeries;
        const seriesLabel =
          series === 'non_expendable'
            ? 'Non-Expendable'
            : series === 'expendable'
              ? 'Expendable'
              : null;

        // Background colors for visual distinction between series
        // Non-Expendable (4000s): subtle background
        // Expendable (5000s): slightly darker background
        const groupBgColor =
          series === 'non_expendable'
            ? (t: import('@mui/material').Theme) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : t.palette.grey[200]
            : series === 'expendable'
              ? (t: import('@mui/material').Theme) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : t.palette.grey[400]
              : undefined;

        const seriesChipSx = {
          fontWeight: 700,
          textTransform: 'uppercase' as const,
          fontSize: '0.7rem',
          border: 'none',
          ...(series === 'non_expendable'
            ? { bgcolor: 'action.selected', color: 'text.primary' }
            : series === 'expendable'
              ? { bgcolor: (t: import('@mui/material').Theme) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : t.palette.grey[500], color: (t: import('@mui/material').Theme) => t.palette.mode === 'dark' ? t.palette.text.primary : '#fff' }
              : {}),
        };

        // Derive receipt types only from assets within this group; fall back to
        // transaction-level types for transactions where per-asset types aren't set.
        const groupAssetReceiptTypes = groupAssets
          .map((a) => a.handReceiptType)
          .filter((t): t is 'PHR' | 'THR' => t === 'PHR' || t === 'THR');
        const groupReceiptTypes =
          groupAssetReceiptTypes.length > 0
            ? (Array.from(new Set(groupAssetReceiptTypes)).sort() as Array<'PHR' | 'THR'>)
            : (displayReceiptTypes ?? []);

        return (
          <Box
            key={compositeKey || 'no-doc-number'}
            sx={(theme) => ({
              bgcolor: typeof groupBgColor === 'function' ? groupBgColor(theme) : groupBgColor,
              borderRadius: groupBgColor ? 1.5 : 0,
              p: groupBgColor ? 1.5 : 0,
            })}
          >
            {documentNumber ? (
              <Box sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={`Doc #${documentNumber}`}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontWeight: 700,
                      '& .MuiChip-label': { px: 1.25 },
                    }}
                  />
                  {seriesLabel && <Chip size="small" label={seriesLabel} sx={seriesChipSx} />}
                  {groupReceiptTypes.length > 0 &&
                    groupReceiptTypes.map((type) => (
                      <Chip
                        key={type}
                        size="small"
                        label={type}
                        variant="outlined"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: '0.05em',
                          ...(type === 'PHR'
                            ? {
                                borderColor: 'success.main',
                                color: 'success.main',
                                bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(63, 127, 85, 0.18)' : 'success.50',
                              }
                            : {
                                borderColor: 'warning.main',
                                color: 'warning.main',
                                bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(193, 125, 31, 0.18)' : 'warning.50',
                              }),
                        }}
                      />
                    ))}
                  <Typography variant="caption" color="text.secondary">
                    ({groupAssets.length} {groupAssets.length === 1 ? 'item' : 'items'})
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Other Items ({groupAssets.length})
              </Typography>
            )}
            <Stack spacing={1.25}>
              {groupAssets.map((asset) => (
                <AssetCard
                  key={asset.assetId}
                  asset={asset}
                  displayReceiptTypes={displayReceiptTypes}
                />
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export interface TransactionDetailModalProps {
  open: boolean;
  transactionId: string | null;
  initialSection?: TransactionDetailSection;
  onClose: () => void;
  formatDateTime: (value: string | null | undefined) => string;
}

const ACCEPTED_FILE_TYPES =
  '.pdf,.jpg,.jpeg,.png,.docx,application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  open,
  transactionId,
  initialSection,
  onClose,
  formatDateTime,
}) => {
  const { isAdmin } = useAuth();

  const { data: transaction = null, isError: isDetailError, error: detailError } = useTransactionLogDetail(
    open ? transactionId : null,
  );

  const activeSections = useMemo<TransactionDetailSection[]>(
    () =>
      isAdmin
        ? [...BASE_SECTIONS.slice(0, 4), 'payloads', ...BASE_SECTIONS.slice(4)]
        : BASE_SECTIONS,
    [isAdmin],
  );

  const [expanded, setExpanded] = useState<Record<TransactionDetailSection, boolean>>({
    summary: true,
    context: false,
    assets: false,
    documents: false,
    payloads: false,
    upload: false,
  });

  const notifications = useNotifications();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevTransactionIdRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);
  const pendingInitialNotesRef = useRef('');
  const notesSentRef = useRef<string | null>(null);
  const notesInitializedFromServerRef = useRef(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [lastSavedNotes, setLastSavedNotes] = useState('');
  const [notesHydrated, setNotesHydrated] = useState(false);
  const [notesSaveState, setNotesSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>(
    'idle',
  );
  const [notesSaveError, setNotesSaveError] = useState<string | null>(null);

  const uploadMutation = useUploadTransactionDocument();
  const updateNotesMutation = useUpdateTransactionNotes();
  const isViewer = useWriteGuard();
  const debouncedNotes = useDebouncedValue(notesDraft, 500);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !transactionId) {
        return;
      }

      setUploadSuccess(null);

      try {
        await uploadMutation.mutateAsync({
          transactionId,
          file,
        });
        setUploadSuccess(file.name);
      } catch {
        // Error is handled by mutation state
      }

      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [transactionId, uploadMutation],
  );

  const handleChooseFilesClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useEffect(() => {
    const openedNow = open && !wasOpenRef.current;
    const switchedTransaction = open && prevTransactionIdRef.current !== transactionId;
    wasOpenRef.current = open;

    if (!open) {
      // Reset upload state when modal closes
      setUploadSuccess(null);
      setNotesHydrated(false);
      notesInitializedFromServerRef.current = false;
      return;
    }

    if (!openedNow && !switchedTransaction) {
      // Detail data arrived after the modal was already open — initialize
      // notes from server if we haven't yet (i.e., the first run
      // saw transaction as null due to lazy loading).
      if (!notesInitializedFromServerRef.current && transaction) {
        notesInitializedFromServerRef.current = true;
        const serverNotes = transaction.notes ?? '';
        pendingInitialNotesRef.current = serverNotes;
        setNotesDraft(serverNotes);
        setLastSavedNotes(serverNotes);
        setNotesSaveError(null);
        setNotesSaveState('idle');
        setNotesHydrated(false);
      }
      return;
    }

    const next: Record<TransactionDetailSection, boolean> = {
      summary: true,
      context: false,
      assets: false,
      documents: false,
      payloads: false,
      upload: false,
    };
    if (initialSection) {
      next[initialSection] = true;
    }
    setExpanded(next);
    const initialNotes = transaction?.notes ?? '';
    pendingInitialNotesRef.current = initialNotes;
    setNotesDraft(initialNotes);
    setLastSavedNotes(initialNotes);
    setNotesSaveError(null);
    setNotesSaveState('idle');
    setNotesHydrated(false);
    notesInitializedFromServerRef.current = !!transaction;
    prevTransactionIdRef.current = transactionId;
  }, [open, transactionId, transaction?.notes, initialSection]);

  useEffect(() => {
    if (!open || !transactionId || notesHydrated) {
      return;
    }

    const initialNotes = pendingInitialNotesRef.current;
    const debouncedMatchesCurrentDraft = debouncedNotes === notesDraft;
    const debouncedMatchesInitialNotes = debouncedNotes === initialNotes;
    const userHasEditedNotes = notesDraft !== initialNotes;

    if (
      debouncedMatchesCurrentDraft &&
      lastSavedNotes === initialNotes &&
      (debouncedMatchesInitialNotes || userHasEditedNotes)
    ) {
      setNotesHydrated(true);
    }
  }, [debouncedNotes, lastSavedNotes, notesDraft, notesHydrated, open, transactionId]);

  useEffect(() => {
    if (!open || !transactionId || !notesHydrated) {
      return;
    }

    if (debouncedNotes === lastSavedNotes || updateNotesMutation.isPending) {
      return;
    }

    setNotesSaveError(null);
    setNotesSaveState('saving');

    const normalizedNotes = debouncedNotes.trim();
    const payloadNotes = normalizedNotes.length > 0 ? normalizedNotes : null;
    notesSentRef.current = debouncedNotes;

    updateNotesMutation.mutate(
      { transactionId: transactionId!, notes: payloadNotes },
      {
        onSuccess: (result) => {
          const persistedNotes = result.notes ?? '';
          setLastSavedNotes(persistedNotes);
          setNotesSaveState('saved');
          // Only reconcile the draft if the user hasn't typed anything
          // new since this save was initiated, to avoid clobbering
          // in-flight keystrokes.
          setNotesDraft((current) => {
            const sent = notesSentRef.current;
            return current === sent || current.trim() === persistedNotes
              ? persistedNotes
              : current;
          });
        },
        onError: (error) => {
          setNotesSaveState('error');
          setNotesSaveError(getErrorMessage(error));
        },
      },
    );
  }, [
    debouncedNotes,
    lastSavedNotes,
    notesHydrated,
    open,
    transactionId,
    updateNotesMutation,
    updateNotesMutation.isPending,
  ]);

  useEffect(() => {
    if (notesSaveState !== 'saved') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setNotesSaveState('idle');
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [notesSaveState]);

  const allExpanded = useMemo(
    () => activeSections.every((section) => expanded[section]),
    [expanded, activeSections],
  );

  // Extract unique document numbers for payload lookup.
  // Payload entities are saved BEFORE the transaction exists, so they may not
  // have transaction_id set — we also match by document number instead.
  // Include both the transaction-level number and per-asset numbers.
  const allDocumentNumbers = useMemo(() => {
    const nums = new Set<string>();
    if (transaction?.documentNumber) nums.add(transaction.documentNumber);
    if (transaction?.assets) {
      for (const asset of transaction.assets) {
        if (asset.documentNumber) nums.add(asset.documentNumber);
      }
    }
    return Array.from(nums);
  }, [transaction?.documentNumber, transaction?.assets]);

  // Extract related entity IDs (e.g. loss record IDs) from documents so
  // payload entities created before the transaction can be found.
  const relatedEntityIds = useMemo(() => {
    const ids = new Set<string>();
    if (transaction?.documents) {
      for (const doc of transaction.documents) {
        if (doc.relatedEntityId) ids.add(doc.relatedEntityId);
      }
    }
    return Array.from(ids);
  }, [transaction?.documents]);

  // Use handReceiptTypes array directly from backend.
  // Kiosk (self-service) checkouts suppress PHR/THR badges per requirement.
  const displayReceiptTypes = transaction?.isKiosk ? [] : (transaction?.handReceiptTypes ?? []);
  const hideTransactionTypeChip = shouldHideTransactionTypeForSource(transaction?.sourceWorkflow);
  const hideDocumentTypeChip = shouldHideDocumentTypeChip({
    transactionType: transaction?.transactionType,
    documentType: transaction?.documentType,
    handReceiptTypes: displayReceiptTypes,
  });

  if (!transaction) {
    if (!open) return null;
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={900}>{isDetailError ? 'Error' : 'Loading…'}</Typography>
            <IconButton onClick={onClose} aria-label="Close"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {isDetailError ? (
            <Alert severity="error" sx={{ my: 2 }}>
              {(detailError as { message?: string })?.message ?? 'Failed to load transaction details.'}
            </Alert>
          ) : (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading transaction details…
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack spacing={1}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Typography variant="h6" component="span" fontWeight={900}>
                {transaction.documentNumber}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              {!hideTransactionTypeChip && (
                <TransactionTypeChip
                  value={transaction.transactionType}
                  cancellationScope={transaction.cancellationScope}
                />
              )}
              {transaction.documentType && !hideDocumentTypeChip && (
                <Chip
                  size="small"
                  label={transaction.documentType}
                  sx={{
                    bgcolor: resolveDocumentTypeBg(transaction.documentType),
                    color: 'common.white',
                    fontWeight: 700,
                    '& .MuiChip-label': { px: 1.25 },
                  }}
                />
              )}
              {displayReceiptTypes.map((type) => (
                <Chip
                  key={type}
                  size="small"
                  label={type}
                  variant="outlined"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    bgcolor: (t) => type === 'PHR'
                      ? (t.palette.mode === 'dark' ? 'rgba(63, 127, 85, 0.18)' : 'success.50')
                      : (t.palette.mode === 'dark' ? 'rgba(193, 125, 31, 0.18)' : 'warning.50'),
                    borderColor: type === 'PHR' ? 'success.main' : 'warning.main',
                    color: type === 'PHR' ? 'success.light' : 'warning.light',
                    borderWidth: 1.5,
                    '& .MuiChip-label': { px: 1.25 },
                  }}
                />
              ))}
            </Stack>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                const nextExpanded = activeSections.reduce(
                  (acc, section) => {
                    acc[section] = !allExpanded;
                    return acc;
                  },
                  {} as Record<TransactionDetailSection, boolean>,
                );
                setExpanded(nextExpanded);
              }}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontWeight: 700 }}
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={1.25}>
          <Accordion
            expanded={expanded.summary}
            onChange={(_event, next) => setExpanded((prev) => ({ ...prev, summary: next }))}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={800}>{sectionTitle.summary}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.25}>
                {transaction.logFormNumber ? (
                  <DetailRow label="Log Form Number:" value={transaction.logFormNumber} />
                ) : (
                  <DetailRow label="Member/Custodian:" value={transaction.memberName} />
                )}
                <DetailRow label="Performed By:" value={transaction.performedByName} />
                {transaction.transactionType === 'THR_TO_PHR_CONVERTED' && (
                  <>
                    <DetailRow label="Hand Receipt Change:" value="THR → PHR" />
                    <DetailRow
                      label="Conversion Reason:"
                      value={transaction.conversionReason ?? '—'}
                    />
                    <DetailRow
                      label="Approval Reference:"
                      value={transaction.conversionApprovalReference ?? '—'}
                    />
                    <DetailRow
                      label="Processed By:"
                      value={transaction.conversionApprovedByName ?? '—'}
                    />
                  </>
                )}
                {transaction.transactionType === 'PURCHASE_REQUISITION_CANCEL' &&
                  transaction.cancellationReason && (
                    <DetailRow
                      label="Cancellation Reason:"
                      value={transaction.cancellationReason}
                    />
                  )}
                {transaction.correctedByDocumentNumber && (
                  <>
                    <Divider />
                    <DetailRow
                      label="Correction Status:"
                      value="This transaction has been corrected"
                    />
                    <DetailRow
                      label="Correction Document:"
                      value={transaction.correctedByDocumentNumber}
                    />
                    <DetailRow
                      label="Correction Type:"
                      value={
                        transaction.correctedByCorrectionType === 'FULL_DOCUMENT'
                          ? 'Full Document Correction'
                          : transaction.correctedByCorrectionType === 'ITEM_LEVEL'
                            ? 'Item-Level Correction'
                            : transaction.correctedByCorrectionType ?? '—'
                      }
                    />
                  </>
                )}
                <DetailRow label="Date Created:" value={formatDateTime(transaction.dateCreated)} />
                <DetailRow
                  label="Date Processed:"
                  value={formatDateTime(transaction.dateProcessed)}
                />
                <DetailRow label="Total Assets:" value={transaction.totalAssets} />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 220, fontWeight: 700, pt: 1 }}
                  >
                    Notes:
                  </Typography>
                  <Stack sx={{ flex: 1 }} spacing={0.5}>
                    <TextField
                      value={notesDraft}
                      onChange={(event) => {
                        setNotesDraft(event.target.value);
                        setNotesSaveError(null);
                        if (notesSaveState !== 'saving') {
                          setNotesSaveState('idle');
                        }
                      }}
                      placeholder="Add transaction notes"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={6}
                      disabled={isViewer}
                    />
                    {notesSaveState === 'saving' && (
                      <Typography variant="caption" color="text.secondary">
                        Saving...
                      </Typography>
                    )}
                    {notesSaveState === 'saved' && (
                      <Typography variant="caption" color="success.main">
                        Saved
                      </Typography>
                    )}
                    {notesSaveState === 'error' && notesSaveError && (
                      <Typography variant="caption" color="error.main">
                        {notesSaveError}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.context}
            onChange={(_event, next) => setExpanded((prev) => ({ ...prev, context: next }))}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={800}>{sectionTitle.context}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.25}>
                <DetailRow label="Owner (Source Party):" value={transaction.owner} />
                <DetailRow label="Location (Source):" value={transaction.location} />
                <Divider />
                <DetailRow label="Receiver (Destination Party):" value={transaction.receiver} />
                <DetailRow label="Receiving Location:" value={transaction.receivingLocation} />
                <DetailRow
                  label="Workflow Source:"
                  value={resolveWorkflowSourceLabel({
                    sourceWorkflow: transaction.sourceWorkflow,
                    isScannerApp: transaction.isScannerApp,
                    isKiosk: transaction.isKiosk,
                  })}
                />
                {transaction.quantityContext && transaction.quantityContext.length > 0 && (
                  <>
                    <Divider />
                    <Typography variant="body2" fontWeight={700}>
                      Quantity Context
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Requested</TableCell>
                          <TableCell align="right">Target</TableCell>
                          <TableCell align="right">Issued</TableCell>
                          <TableCell align="right">Variance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transaction.quantityContext.map((entry, index) => (
                          <TableRow key={`${entry.catalogId}-${index}`}>
                            <TableCell>{entry.catalogName ?? entry.catalogId}</TableCell>
                            <TableCell align="right">{entry.requestedQty}</TableCell>
                            <TableCell align="right">{entry.targetQty}</TableCell>
                            <TableCell align="right">{entry.issuedQty}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: entry.variance > 0 ? 'warning.main' : 'success.main' }}
                            >
                              {entry.variance}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.assets}
            onChange={(_event, next) => setExpanded((prev) => ({ ...prev, assets: next }))}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={800}>
                {sectionTitle.assets} ({transaction.totalAssets})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AssetsInvolvedContent
                assets={transaction.assets}
                transactionType={transaction.transactionType}
                displayReceiptTypes={displayReceiptTypes}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded.documents}
            onChange={(_event, next) => setExpanded((prev) => ({ ...prev, documents: next }))}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={800}>
                {sectionTitle.documents} ({transaction.documents.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {transaction.documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No documents attached.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {transaction.documents.map((doc) => (
                    <Box
                      key={doc.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <AttachFile fontSize="small" color="action" />
                        <MUILink
                          component="button"
                          type="button"
                          onClick={() => {
                            const docWindow = preOpenWindow();
                            void openDocumentWithAuth(doc.url, docWindow).then((opened) => {
                              if (!opened) {
                                notifications.error(
                                  'Unable to open document',
                                  'You may not have permission to view this document. Contact your administrator if you believe this is an error.',
                                );
                              }
                            });
                          }}
                          underline="hover"
                          sx={{
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                          }}
                        >
                          {doc.formType && (transaction.documentNumber || doc.documentNumber)
                            ? `${doc.formType}-${transaction.documentNumber || doc.documentNumber}`
                            : transaction.documentNumber || doc.documentNumber || doc.filename}
                        </MUILink>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {doc.fileSize}
                        </Typography>
                        <Chip
                          size="small"
                          label={doc.formType || doc.documentType}
                          variant="outlined"
                          sx={{ textTransform: 'uppercase', fontWeight: 800 }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>

          {isAdmin && (
            <Accordion
              expanded={expanded.payloads}
              onChange={(_event, next) => setExpanded((prev) => ({ ...prev, payloads: next }))}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={800}>{sectionTitle.payloads}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <DocumentPayloadsSection
                  transactionId={transaction.id}
                  documentNumbers={allDocumentNumbers}
                  relatedEntityIds={relatedEntityIds}
                  formatDateTime={formatDateTime}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {!isViewer && (
          <Accordion
            expanded={expanded.upload}
            onChange={(_event, next) => setExpanded((prev) => ({ ...prev, upload: next }))}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={800}>{sectionTitle.upload}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CloudUpload color="action" />
                  <Typography variant="body2">
                    Upload additional documents for this transaction
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Accepted formats: PDF, JPG, PNG, DOCX (max 25MB)
                </Typography>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  aria-label="Upload document file"
                />

                <Button
                  variant="contained"
                  disabled={uploadMutation.isPending}
                  onClick={handleChooseFilesClick}
                  startIcon={
                    uploadMutation.isPending ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CloudUpload />
                    )
                  }
                  sx={{ width: 'fit-content' }}
                >
                  {uploadMutation.isPending ? 'Uploading...' : 'Choose Files'}
                </Button>

                {uploadMutation.isError && (
                  <Alert severity="error" icon={<ErrorIcon />}>
                    {uploadMutation.error instanceof globalThis.Error
                      ? uploadMutation.error.message
                      : 'Failed to upload document. Please try again.'}
                  </Alert>
                )}

                {uploadSuccess && (
                  <Alert severity="success" icon={<CheckCircle />}>
                    Successfully uploaded: {uploadSuccess}
                  </Alert>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
